
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  UserPlus, 
  Trophy, 
  MessageSquare, 
  Users, 
  Megaphone,
  Shield,
  Calendar,
  CalendarCheck,
  Mail,
  UserCheck,
  UserX,
  Swords,
  X,
  Clock,
  CheckCircle2,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import { Notification } from '@/hooks/useNotifications';
import { getInitials } from '@/utils/stringUtils';
import { useNavigate } from 'react-router-dom';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  showIcon?: boolean;
}

const NotificationItem = ({ notification, onMarkAsRead, showIcon = false }: NotificationItemProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Handle navigation based on notification type
    switch (notification.type) {
      case 'group_invitation':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}`);
        }
        break;
      case 'group_message':
        if (notification.content?.group_id) {
          navigate(`/messages/group/${notification.content.group_id}`);
        }
        break;
      case 'group_announcement':
      case 'event_reminder_24h':
      case 'event_reminder_1h':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/announcements`);
        }
        break;
      case 'private_message':
        if (notification.content?.sender_id) {
          navigate(`/messages/user/${notification.content.sender_id}`);
        }
        break;
      case 'tournament_created':
      case 'tournament_opened':
      case 'tournament_closed':
      case 'tournament_reminder':
      case 'tournament_matches_ready':
        if (notification.content?.tournament_id && notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/tournaments/${notification.content.tournament_id}`);
        } else if (notification.content?.tournament_id) {
          navigate(`/tournaments/${notification.content.tournament_id}`);
        }
        break;
      case 'tournament_team_invitation':
        if (notification.content?.tournament_id) {
          navigate(`/tournaments/${notification.content.tournament_id}`);
        }
        break;
      case 'tournament_team_response':
        if (notification.content?.tournament_id) {
          navigate(`/tournaments/${notification.content.tournament_id}`);
        }
        break;
      case 'availability_poll_created':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}?tab=polls`);
        }
        break;
      case 'match_scheduled':
      case 'match_cancelled':
      case 'match_modified':
        navigate('/matches');
        break;
      case 'open_match_available':
        navigate(
          notification.content?.published_match_id
            ? `/matches?highlight=${notification.content.published_match_id}&tab=open`
            : '/matches?tab=open'
        );
        break;
      case 'match_validation':
        navigate('/matches');
        break;
      case 'match_validated':
        navigate('/matches');
        break;
      case 'new_group_member':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}`);
        }
        break;
      case 'challenge_received':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/stats`);
        }
        break;
      case 'challenge_cancelled':
      case 'challenge_scheduled':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/stats`);
        }
        break;
      case 'ladder_decay':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/stats`);
        }
        break;
      case 'challenge_reminder':
        if (notification.content?.group_id) {
          navigate(`/groups/${notification.content.group_id}/stats?tab=challenges`);
        }
        break;
      case 'competitive_digest':
        if (notification.content?.group_id) {
          const tab = notification.content?.tab === 'challenges' ? 'challenges' : 'rankings';
          navigate(`/groups/${notification.content.group_id}/stats?tab=${tab}`);
        }
        break;
      case 'rank_movement':
        if (notification.content?.group_id) {
          const rankTab = notification.content?.tab === 'pairs' ? 'pairs' : 'rankings';
          navigate(`/groups/${notification.content.group_id}/stats?tab=${rankTab}`);
        }
        break;
      case 'upcoming_match_reminder':
      case 'score_capture_reminder': {
        // Support both old key ('published_match_id') and new key ('match_id')
        const matchId = notification.content?.match_id ?? notification.content?.published_match_id;
        if (matchId) {
          navigate(`/matches?highlight=${matchId}&tab=scheduled`);
        } else {
          navigate('/matches');
        }
        break;
      }
      default:
        // Default navigation or no navigation
        break;
    }
  };

  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'group_invitation':
        return <UserPlus className="h-4 w-4 text-blue-500" />;
      case 'group_message':
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      case 'group_announcement':
        return <Megaphone className="h-4 w-4 text-blue-600" />;
      case 'event_reminder_24h':
      case 'event_reminder_1h':
        return <Calendar className="h-4 w-4 text-amber-500" />;
      case 'private_message':
        return <Mail className="h-4 w-4 text-purple-500" />;
      case 'user_joined_group':
      case 'new_group_member':
        return <UserPlus className="h-4 w-4 text-green-500" />;
      case 'tournament_created':
      case 'tournament_opened':
      case 'tournament_closed':
      case 'tournament_reminder':
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 'tournament_team_invitation':
        return <UserPlus className="h-4 w-4 text-padel-green-light" />;
      case 'tournament_team_response':
        return notification.content?.response === 'accepted' ? 
          <UserCheck className="h-4 w-4 text-green-500" /> : 
          <UserX className="h-4 w-4 text-red-500" />;
      case 'availability_poll_created':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'match_scheduled':
      case 'match_modified':
      case 'open_match_available':
        return <Users className="h-4 w-4 text-padel-green-light" />;
      case 'match_cancelled':
        return <UserX className="h-4 w-4 text-red-500" />;
      case 'match_validation':
        return <Calendar className="h-4 w-4 text-orange-500" />;
      case 'match_validated':
        return <CheckCircle2 className="h-4 w-4 text-padel-green-light" />;
      case 'security_alert':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'challenge_received':
        return <Swords className="h-4 w-4 text-orange-500" />;
      case 'challenge_cancelled':
        return <X className="h-4 w-4 text-destructive" />;
      case 'challenge_scheduled':
        return <CalendarCheck className="h-4 w-4 text-blue-500" />;
      case 'upcoming_match_reminder':
        return <Calendar className="h-4 w-4 text-primary" />;
      case 'score_capture_reminder':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'challenge_reminder':
        return <Swords className="h-4 w-4 text-primary" />;
      case 'ladder_decay':
        return <TrendingDown className="h-4 w-4 text-orange-500" />;
      case 'competitive_digest':
        return <Trophy className="h-4 w-4 text-primary" />;
      case 'rank_movement':
        return notification.content?.direction === 'down'
          ? <TrendingDown className="h-4 w-4 text-orange-500" />
          : <TrendingUp className="h-4 w-4 text-primary" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getNotificationColor = () => {
    switch (notification.type) {
      case 'group_announcement':
        return 'border-l-accent bg-accent/10';
      case 'event_reminder_24h':
      case 'event_reminder_1h':
        return 'border-l-amber-500 bg-amber-50 dark:bg-amber-950/30';
      case 'group_message':
        return 'border-l-success bg-success/10';
      case 'private_message':
        return 'border-l-purple-500 bg-purple-50 dark:bg-purple-950/30';
      case 'security_alert':
        return 'border-l-destructive bg-destructive/10';
      case 'tournament_created':
      case 'tournament_opened':
      case 'tournament_closed':
      case 'tournament_reminder':
        return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/30';
      case 'tournament_team_invitation':
        return 'border-l-primary bg-primary/10';
      case 'tournament_team_response':
        return notification.content?.response === 'accepted' ? 
          'border-l-success bg-success/10' : 
          'border-l-destructive bg-destructive/10';
      case 'match_scheduled':
      case 'match_modified':
      case 'open_match_available':
        return 'border-l-primary bg-primary/10';
      case 'match_cancelled':
        return 'border-l-destructive bg-destructive/10';
      case 'new_group_member':
        return 'border-l-green-500 bg-green-50 dark:bg-green-950/30';
      case 'challenge_received':
        return 'border-l-orange-500 bg-orange-50 dark:bg-orange-950/30';
      case 'challenge_cancelled':
        return 'border-l-destructive bg-destructive/10';
      case 'challenge_scheduled':
        return 'border-l-blue-500 bg-blue-50 dark:bg-blue-950/30';
      default:
        return notification.is_read ? 'border-l-muted-foreground' : 'border-l-primary bg-primary/5';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${
        !notification.is_read ? 'bg-primary/5' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {showIcon && (
          <div className="flex-shrink-0 mt-0.5 p-1.5 rounded-full bg-muted">
            {getNotificationIcon()}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium leading-tight ${
              notification.is_read ? 'text-muted-foreground' : 'text-foreground'
            }`}>
              {notification.title}
            </h4>
            {!notification.is_read && (
              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />
            )}
          </div>
          
          <p className={`text-xs mt-1 leading-relaxed line-clamp-2 ${
            notification.is_read ? 'text-muted-foreground/70' : 'text-muted-foreground'
          }`}>
            {notification.message}
          </p>
          
          <span className="text-[10px] text-muted-foreground/60 mt-1.5 block">
            {formatTime(notification.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
